import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

import {
  pb,
  API_BASE_URL,
} from "../lib/pocketbaseConfig";
import { useDeviceType } from "./DeviceTypeContext";

interface PbUserRecord {
  id: string;
  collectionId?: string;
  collectionName?: string;

  firstName?: string;
  lastName?: string;
  email?: string;
  city?: string;
  phone?: string;
  gender?: string;
  age?: string;
  profile?: string;
  created?: string;
  updated?: string;

  emailVerified?: boolean;
  verified?: boolean;

  linkedBrand?: string;
  linkedStore?: string;
  storeProfile?: string;
  brandProfile?: string;

  favorites?: string;
  shoppingList?: string;
  entryType?: string;

  storeId?: string;
  brandId?: string;
  storeName?: string;
  brandName?: string;

  isGuest?: boolean;
  role?: string;
  guestUserId?: string;

  sharedShoppingListId?: string;
  isLocalhost?: boolean;
  deviceType?: string;

  isSuperUser?: boolean;
  tutorial?: boolean;
}

interface AppUser extends PbUserRecord {
  $id: string;
  name: string;
  isGuest: boolean;
  isSuperUser: boolean;
  sharedShoppingListId: string;
  isLocalhost?: boolean;
}

interface UserContextType {
  currentUser: AppUser | null;
  loading: boolean;
  refetchUser: () => Promise<void>;
  shoppingListCount: number;
  setShoppingListCount: (count: number) => void;
  totalShoppingQuantity: number;
  setTotalShoppingQuantity: (count: number) => void;
  refreshShoppingCount: () => Promise<void>;
}

const UserContext = createContext<UserContextType | null>(null);

const GUEST_AUTH_COLLECTION = "guest_users_auth";
const USERS_AUTH_COLLECTION = "users";
const SHOPPING_LIST_COLLECTION = "shopping_list";

export function UserProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  const [shoppingListCount, setShoppingListCount] = useState(0);
  const [totalShoppingQuantity, setTotalShoppingQuantity] = useState(0);

  const deviceType = useDeviceType();

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const isExpired = (endDate?: string): boolean => {
    if (!endDate) return false;

    const end = new Date(endDate);
    const now = new Date();

    end.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    return end < now;
  };

  const makeSharedShoppingListId = (length = 16): string => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);

    let output = "";

    for (let index = 0; index < length; index += 1) {
      output += chars[bytes[index] % chars.length];
    }

    return output;
  };

  const generateGuestId = (): string => {
    return `guest_${
      Math.floor(Math.random() * 90000000) + 10000000
    }`;
  };

  const generateGuestPassword = (): string => {
    return `${crypto.randomUUID()}${crypto.randomUUID()}`;
  };

  const mapPbUserToAppUser = (record: any): AppUser => {
    const firstName = String(record?.firstName || "").trim();
    const lastName = String(record?.lastName || "").trim();

    const isGuest = Boolean(record?.isGuest);
    const isSuperUser = Boolean(
      record?.isSuperUser || record?.role === "admin"
    );

    return {
      ...record,

      id: record.id,
      $id: record.id,

      firstName,
      lastName,

      name:
        `${firstName} ${lastName}`.trim() ||
        firstName ||
        (isGuest ? "Гост" : "User"),

      email: record.email || "",
      city: record.city || "",
      phone: record.phone || "",
      gender: record.gender || "",
      age: record.age || "",
      profile: record.profile || "",

      created: record.created || "",
      updated: record.updated || "",

      emailVerified: Boolean(
        record.emailVerified ?? record.verified
      ),

      verified: Boolean(record.verified),

      linkedBrand: record.linkedBrand || "",
      linkedStore: record.linkedStore || "",
      storeProfile: record.storeProfile || "",
      brandProfile: record.brandProfile || "",

      favorites: record.favorites || "[]",
      shoppingList: record.shoppingList || "[]",
      entryType: record.entryType || (isGuest ? "guest" : "user"),

      storeId: record.storeId || "",
      brandId: record.brandId || "",
      storeName: record.storeName || "",
      brandName: record.brandName || "",

      isGuest,
      role: record.role || (isGuest ? "user" : ""),
      guestUserId: record.guestUserId || "",

      sharedShoppingListId: record.sharedShoppingListId || "",
      isLocalhost: Boolean(record.isLocalhost),
      deviceType: record.deviceType || "",

      isSuperUser,
    };
  };

  const createLocalFallbackGuest = (
    guestUserId: string,
    guestEmail: string
  ): AppUser => {
    return {
      id: guestUserId,
      $id: guestUserId,

      firstName: "",
      lastName: "",
      email: guestEmail,

      favorites: JSON.stringify([]),
      shoppingList: JSON.stringify([]),
      entryType: "guest",

      isGuest: true,
      isSuperUser: false,
      role: "user",

      guestUserId,
      sharedShoppingListId: "",
      deviceType,
      isLocalhost,

      name: "Гост",
    };
  };

  const ensureGuestUser = async (): Promise<void> => {
    let guestUserId = localStorage.getItem("guestUserId");

    if (!guestUserId) {
      guestUserId = generateGuestId();
      localStorage.setItem("guestUserId", guestUserId);
    }

    let guestEmail = localStorage.getItem("guestUserEmail");
    let guestPassword = localStorage.getItem("guestUserPassword");

    if (!guestEmail) {
      guestEmail = `${guestUserId}@guest.invalid`;
      localStorage.setItem("guestUserEmail", guestEmail);
    }

    if (!guestPassword) {
      guestPassword = generateGuestPassword();
      localStorage.setItem("guestUserPassword", guestPassword);
    }

    const guestData = {
      email: guestEmail,
      emailVisibility: false,
      password: guestPassword,
      passwordConfirm: guestPassword,

      firstName: "",
      lastName: "",

      favorites: JSON.stringify([]),
      shoppingList: JSON.stringify([]),

      entryType: "guest",
      isGuest: true,
      role: "user",

      guestUserId,
      sharedShoppingListId: makeSharedShoppingListId(16),

      deviceType,
      isLocalhost,
    };

    try {
      let guestRecord: any;

      try {
        console.log("[auth] authenticating existing guest");

        const authResult = await pb
          .collection(GUEST_AUTH_COLLECTION)
          .authWithPassword(guestEmail, guestPassword);

        guestRecord = authResult.record;
      } catch (loginError: any) {
        console.log(
          "[auth] existing guest not found, creating guest auth record"
        );

        console.log(
          "[auth] guest login error:",
          loginError?.response?.data || loginError?.message
        );

        guestRecord = await pb
          .collection(GUEST_AUTH_COLLECTION)
          .create(guestData);

        const authResult = await pb
          .collection(GUEST_AUTH_COLLECTION)
          .authWithPassword(guestEmail, guestPassword);

        guestRecord = authResult.record;
      }

      const mappedGuest = mapPbUserToAppUser(guestRecord);

      setCurrentUser({
        ...mappedGuest,

        id: guestRecord.id,
        $id: guestRecord.id,

        isGuest: true,
        isSuperUser: false,
        role: "user",
        isLocalhost,

        name:
          `${guestRecord.firstName || ""} ${
            guestRecord.lastName || ""
          }`.trim() || "Гост",
      });

      console.log("[auth] guest authenticated:", guestRecord.id);
    } catch (error: any) {
      console.error("[auth] guest create/login failed:", error);
      console.error(
        "[auth] guest error data:",
        error?.response?.data || error?.data
      );

      /*
       * This fallback is local-only.
       * It does not have a PocketBase auth token and therefore cannot
       * safely create or modify protected PocketBase records.
       */
      const fallbackGuest = createLocalFallbackGuest(
        guestUserId,
        guestEmail
      );

      setCurrentUser(fallbackGuest);
    }
  };

const refreshShoppingCount = useCallback(async (): Promise<void> => {
  if (!currentUser?.$id) {
    setShoppingListCount(0);
    setTotalShoppingQuantity(0);
    return;
  }

  const token = pb.authStore.token;

  if (!token) {
    setShoppingListCount(0);
    setTotalShoppingQuantity(0);
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/worker-api/shopping-list-count`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Shopping count request failed: ${response.status}`
      );
    }

    const data = await response.json();

    setShoppingListCount(
      Math.max(0, Number(data.count) || 0)
    );

    setTotalShoppingQuantity(
      Math.max(0, Number(data.totalQuantity) || 0)
    );
  } catch (error) {
    console.error(
      "Failed to refresh shopping count:",
      error
    );

    setShoppingListCount(0);
    setTotalShoppingQuantity(0);
  }
}, [currentUser?.$id]);

  const loadRegisteredUser = async (
    authUser: any
  ): Promise<void> => {
    let databaseUser: any;

    try {
      databaseUser = await pb
        .collection(USERS_AUTH_COLLECTION)
        .getOne(authUser.id);
    } catch {
      databaseUser = await pb
        .collection(USERS_AUTH_COLLECTION)
        .create({
          idAuth: authUser.id,
          firstName: authUser.firstName || "",
          lastName: authUser.lastName || "",
          email: authUser.email || "",

          emailVerified: Boolean(authUser.verified),
          entryType: "user",

          favorites: JSON.stringify([]),
          shoppingList: JSON.stringify([]),
          sharedShoppingListId: makeSharedShoppingListId(16),

          isGuest: false,
          isSuperUser: false,
          role: "user",
        });
    }

    setCurrentUser({
      ...mapPbUserToAppUser(databaseUser),

      id: databaseUser.id,
      $id: databaseUser.id,

      emailVerified:
        databaseUser.emailVerified ?? Boolean(authUser.verified),

      isGuest: false,
      isSuperUser:
        Boolean(databaseUser.isSuperUser) ||
        databaseUser.role === "admin",
    });
  };

  const loadGuestAuthUser = async (
    authUser: any
  ): Promise<void> => {
    const guestRecord = await pb
      .collection(GUEST_AUTH_COLLECTION)
      .getOne(authUser.id);

    const mappedGuest = mapPbUserToAppUser(guestRecord);

    setCurrentUser({
      ...mappedGuest,

      id: guestRecord.id,
      $id: guestRecord.id,

      isGuest: true,
      isSuperUser: false,
      role: "user",
      isLocalhost,

      name:
        `${guestRecord.firstName || ""} ${
          guestRecord.lastName || ""
        }`.trim() || "Гост",
    });
  };

  const refetchUser = async (): Promise<void> => {
    setLoading(true);

    try {
      if (pb.authStore.isValid && pb.authStore.model) {
        const authUser = pb.authStore.model as any;

        const collectionName =
          authUser.collectionName || "";

        if (collectionName === GUEST_AUTH_COLLECTION) {
          await loadGuestAuthUser(authUser);
        } else if (
          collectionName === USERS_AUTH_COLLECTION ||
          collectionName === ""
        ) {
          await loadRegisteredUser(authUser);
        } else {
          console.warn(
            "[auth] unknown auth collection:",
            collectionName
          );

          await loadRegisteredUser(authUser);
        }
      } else {
        await ensureGuestUser();
      }
    } catch (error) {
      console.error("refetchUser failed:", error);

      pb.authStore.clear();
      await ensureGuestUser();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refetchUser();
  }, []);

  useEffect(() => {
    void refreshShoppingCount();
  }, [refreshShoppingCount]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        loading,

        refetchUser,

        shoppingListCount,
        setShoppingListCount,

        totalShoppingQuantity,
        setTotalShoppingQuantity,

        refreshShoppingCount,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error(
      "useUser must be used within UserProvider"
    );
  }

  return context;
};